import { ORDER_REASON_MAX_LENGTH } from '@aysar/contracts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { formatMoney } from '@/shared/utils/format-money/format-money.util';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import {
  useOrderQuery,
  useRefundOrderMutation,
  useRejectOrderMutation,
} from '../../services/payments/payments.api';
import { paymentErrorCopy } from '../../utils/payment-error/payment-error.util';

/** The two decisions that require a reason, and therefore a dialog of their own. */
export type ReasonedAction = 'reject' | 'refund';

export interface UseOrderDecisionModalLogicInput {
  orderId: string;
  action: ReasonedAction;
  onDone: () => void;
}

export interface ReasonValues {
  reason: string;
}

export interface UseOrderDecisionModalLogic {
  title: string;
  description: string;
  submitLabel: string;
  fields: FieldDef[];
  schema: z.ZodType<ReasonValues, ReasonValues>;
  initialValues: ReasonValues;
  /** Withholds the form until the order is known — RHF reads defaults once. */
  isLoading: boolean;
  /** The refusal, translated, or `null`. Drawn INSIDE the dialog. */
  error: string | null;
  isConflict: boolean;
  isGone: boolean;
  reload: () => void;
  submit: (values: ReasonValues) => Promise<void>;
}

/**
 * Rejecting an order, or recording a refund against one.
 *
 * ## Two acts, one dialog, and deliberately not one act with a flag
 *
 * They share a shape — a required reason and an irreversible write — and nothing
 * else: a rejection **releases** the member's reserved discounts, a refund
 * **revokes** their subscription and leaves those discounts spent
 * (`REFUND_KEEPS_GRANTS_SPENT`). So every sentence differs, and the `action`
 * chooses the whole copy set rather than toggling a word in it.
 *
 * ## The reason is REQUIRED, which is why this is not a confirmation
 *
 * `DirectPaymentOrderRejectSchema` and its refund twin both take
 * `reason: z.string().trim().min(1)`. A required field belongs to a second
 * dialog, never to an optional key on a confirmation (P20b) — and the api's own
 * argument is that this is the branch where a member paid, or believes they
 * paid, and got nothing.
 *
 * ## It re-reads the order rather than being handed one
 *
 * `ModalData` is serializable-only, so what travels is the id. That is also the
 * safer design: a row object smuggled through the store would be a snapshot of a
 * status a colleague may have changed since the queue last rendered.
 */
export function useOrderDecisionModalLogic({
  orderId,
  action,
  onDone,
}: UseOrderDecisionModalLogicInput): UseOrderDecisionModalLogic {
  const { t } = useTranslation('payments');
  const { locale } = useDirection();
  const toast = useToast();
  const [refusal, setRefusal] = useState<ApiError | null>(null);

  const query = useOrderQuery(orderId, { skip: orderId === '' });
  const [rejectOrder] = useRejectOrderMutation();
  const [refundOrder] = useRefundOrderMutation();

  const order = query.data;

  const schema = useMemo(
    () =>
      z
        .object({
          // The contract's own rule, restated rather than imported because the
          // two write schemas are identical and neither is the "reason schema" —
          // the length constant IS the shared source (U3).
          reason: z.string().trim().min(1).max(ORDER_REASON_MAX_LENGTH),
        })
        .strict(),
    [],
  );

  const fields = useMemo<FieldDef[]>(
    () => [
      {
        name: 'reason',
        kind: 'textarea',
        label: t(`${action}.reason`),
        description: t(`${action}.reasonHint`),
        isRequired: true,
        rows: 3,
        maxLength: ORDER_REASON_MAX_LENGTH,
        colSpan: 'full',
      },
    ],
    [t, action],
  );

  // The kind chooses the sentence (D9b2): refunding a plan revokes a
  // subscription, refunding a fee marks the ledger row refunded. Until the
  // order is read there is no kind and no amount, so a neutral line stands in
  // rather than a template with its placeholder showing.
  const description = useMemo(() => {
    if (order === undefined) {
      return t(`${action}.descriptionPending`);
    }

    return t(`${action}.description.${order.kind}`, {
      amount: formatMoney(order.chargedAmount, locale, order.currency),
    });
  }, [t, action, order, locale]);

  const copy = paymentErrorCopy(refusal);

  const submit = useCallback(
    async (values: ReasonValues) => {
      const run = action === 'reject' ? rejectOrder : refundOrder;

      try {
        const decided = await run({ id: orderId, reason: values.reason }).unwrap();
        setRefusal(null);
        // The kind off the api's answer, not the cached read: a fee's refund
        // voids a ticket where a plan's revokes a subscription (D9b2).
        toast.success(
          action === 'reject' ? t('done.rejected') : t(`done.refunded.${decided.kind}`),
        );
        onDone();
      } catch (reason) {
        // Held in state and drawn in the dialog rather than thrown at the form's
        // field mapper: `billing_order_not_claimable` names no field, and a
        // form-level sentence would scroll past under the reason box instead of
        // offering the re-read the operator actually needs.
        setRefusal(toApiError(reason));
      }
    },
    [action, orderId, rejectOrder, refundOrder, toast, t, onDone],
  );

  return {
    title: t(`${action}.title`),
    description,
    submitLabel: t(`${action}.confirm`),
    fields,
    schema,
    initialValues: { reason: '' },
    // Withheld until the order arrives: RHF reads `defaultValues` once, and the
    // description names an amount the dialog would otherwise open without.
    isLoading: query.isLoading,
    error:
      refusal === null
        ? null
        : t(copy?.i18nKey ?? 'error.generic', translationArgs(copy, t, locale)),
    isConflict: copy?.isConflict ?? false,
    isGone: copy?.isGone ?? false,
    reload: () => {
      setRefusal(null);
      void query.refetch();
    },
    submit,
  };
}

/**
 * The status a claim found, translated and lower-cased so it reads inside the
 * sentence rather than as a label pasted into it.
 */
function translationArgs(
  copy: ReturnType<typeof paymentErrorCopy>,
  t: (key: string) => string,
  locale: string,
): Record<string, string> {
  if (copy?.foundStatus == null) {
    return {};
  }

  return { status: t(`status.${copy.foundStatus}`).toLocaleLowerCase(locale) };
}
